import { useEffect, useRef, useState } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
  searchText: string;
}

interface Props {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = '-- Buscar --',
  className = '',
}: Props) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef(options);

  // Keep optionsRef in sync without affecting dependencies
  useEffect(() => {
    optionsRef.current = options;
  });

  // Sync input display when value changes externally (e.g. modal reset)
  useEffect(() => {
    if (!value) {
      setInputValue('');
    } else {
      const match = optionsRef.current.find(o => o.value === value);
      if (match) setInputValue(match.label);
    }
  }, [value]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const filtered = inputValue
    ? options.filter(o => o.searchText.toLowerCase().includes(inputValue.toLowerCase()))
    : options;

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const term = e.target.value;
    setInputValue(term);
    setIsOpen(true);
    if (!term) onChange('');
  }

  function handleSelect(option: SearchableSelectOption) {
    setInputValue(option.label);
    onChange(option.value);
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        autoComplete="off"
      />
      {isOpen && filtered.length > 0 && (
        <ul className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto mt-1">
          {filtered.map(option => (
            <li
              key={option.value}
              onMouseDown={() => handleSelect(option)}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-amber-50 ${
                option.value === value ? 'bg-amber-100 font-medium' : ''
              }`}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
